// LocalVideoDisplay.jsx
import React, { useEffect, useState } from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { videoStorage, auth, db } from '../../../../firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoffee, faHorse } from '@fortawesome/free-solid-svg-icons';
import './localWorkspace.css';
import './localVideoDisplay.css';
import placeholder_dark_thumbnail from '../../../../assets/images/thumbnail/placeholder-dark-thumbnail.jpg';

const bumpQuips = [
  'bump-1.0 is crunching frames like popcorn',
  'bump-1.0 is deep in binge-watch mode',
  'bump-1.0 spotted a highlight reel',
  'bump-1.0 decoding pixels at warp speed',
  'bump-1.0 is vibing with your video',
  'bump-1.0 is surfing video waves',
  'bump-1.0 in full video snack mode',
  'bump-1.0 finding golden nuggets in footage',
  'bump-1.0 diving into the pixels',
  'bump-1.0 analyzing clips with swag',
  'bump-1.0 is surfing the video stream',
  'bump-1.0 crunching pixels for breakfast',
  'bump-1.0 vibing through your video frames',
  'bump-1.0 just spotted something epic',
  'bump-1.0 getting cozy with this footage',
  'bump-1.0 catching video waves',
  'bump-1.0 on a pixel-powered mission',
  'bump-1.0 busy turning clips into magic',
  'bump-1.0 tuning into your highlights',
  'bump-1.0 making sense of moving pictures',
];

const LocalVideoDisplay = ({
  content_id,
  isLocalTranscriptAvailable,
  isLOCALProcessing,
  isTranscriptEmpty,
}) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [videoQuality, setVideoQuality] = useState('');
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [percent, setPercent] = useState(0);

  // Initialize to a random quip index
  const [quipIndex, setQuipIndex] = useState(
    () => Math.floor(Math.random() * bumpQuips.length)
  );

  const user = auth.currentUser;

  // 1) Fetch video URL
  useEffect(() => {
    if (!user) return;
    const fetchVideoUrl = async () => {
      const videoRef = ref(videoStorage, `${user.uid}/workspace-${content_id}`);
      try {
        const url = await getDownloadURL(videoRef);
        setVideoUrl(url);
      } catch (err) {
        console.error('Failed to load video URL', err);
      }
    };
    fetchVideoUrl();
  }, [user, content_id]);

  // 2) Fetch metadata (localStorage or Firestore)
  useEffect(() => {
    if (!isLocalTranscriptAvailable && !isLOCALProcessing) return;
    const localKey = `videometadata_${content_id}`;

    const fetchFromFirestore = async () => {
      if (!user) return;
      try {
        const userDoc = doc(db, 'users', user.uid);
        const wsDoc = doc(userDoc, 'workspaceContent', `workspace-${content_id}`);
        const snap = await getDoc(wsDoc);
        if (snap.exists()) {
          const data = snap.data();
          const meta = {
            video_file_name: data.video_file_name || '',
            video_quality: data.video_quality || '',
          };
          setVideoTitle(meta.video_file_name);
          setVideoQuality(meta.video_quality);
          localStorage.setItem(localKey, JSON.stringify(meta));
        }
      } catch (err) {
        console.error('Failed to fetch metadata', err);
      }
    };

    const cached = localStorage.getItem(localKey);
    if (cached) {
      try {
        const { video_file_name, video_quality } = JSON.parse(cached);
        setVideoTitle(video_file_name);
        setVideoQuality(video_quality);
      } catch {
        localStorage.removeItem(localKey);
        fetchFromFirestore();
      }
    } else {
      fetchFromFirestore();
    }
  }, [user, content_id, isLocalTranscriptAvailable, isLOCALProcessing]);

  // 3) Firestore listener for bump progress
  useEffect(() => {
    if (!user || !isLOCALProcessing) return;
    const wsRef = doc(db, 'users', user.uid, 'workspaceContent', `workspace-${content_id}`);
    const unsub = onSnapshot(wsRef, (snap) => {
      if (!snap.exists()) return;
      const d = snap.data();
      const total = (d.total_audio_chunks || 0) + (d.expected_video_chunks || 0);
      const rec = (d.received_audio_chunks || 0) + (d.received_video_chunks || 0);
      const realPct = total > 0 ? Math.min(100, Math.round((rec / total) * 100)) : 0;
      // never go backwards
      setPercent(prev => Math.max(prev, realPct));
    });
    return () => unsub();
  }, [user, content_id, isLOCALProcessing]);

  // 4) Prime at 25% when processing starts, reset to 0 when it stops
  useEffect(() => {
    if (isLOCALProcessing) {
      setPercent(25);
    } else {
      setPercent(0);
    }
  }, [isLOCALProcessing]);

  // 5) Every 7s pick a random quip
  useEffect(() => {
    if (!isLOCALProcessing) return;
    const iv = setInterval(() => {
      setQuipIndex(() => Math.floor(Math.random() * bumpQuips.length));
    }, 7000);
    return () => clearInterval(iv);
  }, [isLOCALProcessing]);

  const truncate = (name) =>
    name.length > 28 ? `${name.slice(0, 28)}...` : name;
  const handleVideoLoaded = () => setIsVideoLoaded(true);

  return (
    <div className="localworks-video-section">
      {/* Video Preview */}
      <div className="localworks-video-preview">
        {videoUrl ? (
          <>
            {!isVideoLoaded && (
              <img
                src={placeholder_dark_thumbnail}
                alt="Video Thumbnail"
                className="localworks-video-preview-placeholder"
              />
            )}
            <video
              src={videoUrl}
              controls
              className="localworks-video-preview-media"
              onLoadedData={handleVideoLoaded}
              style={{ display: isVideoLoaded ? 'block' : 'none' }}
            />
          </>
        ) : (
          <img
            src={placeholder_dark_thumbnail}
            alt="Video Thumbnail"
            className="localworks-video-preview-placeholder"
          />
        )}
      </div>

      {/* Info / Bump Progress */}
      <div className="localworks-video-info">
        {isLOCALProcessing ? (
          <div className="localworks-bump-progress-wrapper">
            <div className="bump-progress-bar">
              <div
                className="bump-progress-fill"
                style={{ width: `${percent}%` }}
              />
              <div
                className="bump-progress-flag"
                style={{ left: `${percent}%` }}
              />
              {/* Racing horse above the bar */}
              <FontAwesomeIcon
                icon={faHorse}
                className="bump-progress-horse"
                style={{ left: `${percent}%` }}
              />
            </div>
            <div className="bump-progress-text">
              {bumpQuips[quipIndex]}{' '}
              <FontAwesomeIcon icon={faCoffee} className="bump-icon-coffee" />{' '}
              <span className="bump-percent">({percent}%)</span>
            </div>
          </div>
        ) : (
          <>
            <div className="localworks-filename-container">
              <div className="localworks-filename-label">Filename</div>
              <div className="editBump-section-filename">
                {truncate(videoTitle)}
              </div>
            </div>
            <div className="localworks-quality-container">
              <div className="localworks-quality">{videoQuality}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LocalVideoDisplay;
